import { useMemo } from 'react';
import type { TransactionRuleConfig } from '@capture/shared';
import { useFormConfigStore } from '../state/store';
import { useRemittanceIssues } from './useRemittanceIssues';
import { buildPickerData } from './remittance-picker-data';
import { ExpressionEditor, DEFAULT_PICKER_DATA } from '../shared/ExpressionEditor';
import './TransactionRulesSection.css';

const SEVERITY_OPTIONS: { value: TransactionRuleConfig['severity']; caption: string }[] = [
  { value: 'soft', caption: 'soft — flags the transaction (overridable)' },
  { value: 'hard', caption: 'hard — blocks accept' },
];

/** Next `rule_<n>` id: 1 + the max numeric suffix among existing ids that
 *  match `rule_<digits>` exactly. Ids that don't match the pattern (legacy
 *  or hand-authored ids) simply don't contribute to the max. Deterministic —
 *  no Date/random — so re-running the same seed always yields the same id. */
function nextRuleId(rules: TransactionRuleConfig[]): string {
  let max = 0;
  for (const rule of rules) {
    const match = /^rule_(\d+)$/.exec(rule.id);
    if (!match) continue;
    const n = Number(match[1]);
    if (n > max) max = n;
  }
  return `rule_${max + 1}`;
}

/**
 * SP6 Task 7 — Transaction Rules section of the Remittance job-config tab.
 * Renders `remittance.transactionRules`: job-level soft/hard `when`-expression
 * rules evaluated per transaction. Every write recomputes and sends the FULL
 * array via `updateRemittance({ transactionRules: next })` — the store's
 * merge is shallow, so a partial patch would silently drop other rows.
 *
 * Row keys use each rule's own `id` (generated once at add-time via
 * `nextRuleId` and stored in the row, per the shape the config already
 * carries — see `TransactionRuleConfig.id`). That id is stable across edits
 * and across remove-at-index shifts, which is exactly the guarantee
 * BalancePolicySection's synthetic ref-array row ids exist to provide for
 * `DeltaResolutionRule` (which has no id of its own) — since this row shape
 * already has a persisted stable id, keying by it directly is simpler than
 * introducing a parallel client-side id array for the same guarantee.
 */
export function TransactionRulesSection() {
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const updateRemittance = useFormConfigStore((s) => s.updateRemittance);
  const issues = useRemittanceIssues();

  const rules = contentV3?.remittance?.transactionRules ?? [];
  const pickerData = useMemo(() => (contentV3 ? buildPickerData(contentV3) : DEFAULT_PICKER_DATA), [contentV3]);

  function writeRules(next: TransactionRuleConfig[]) {
    updateRemittance({ transactionRules: next });
  }

  function handleAdd() {
    const newRule: TransactionRuleConfig = {
      id: nextRuleId(rules),
      name: '',
      severity: 'soft',
      when: '',
      message: '',
    };
    writeRules([...rules, newRule]);
  }

  function handleRemove(index: number) {
    writeRules(rules.filter((_, i) => i !== index));
  }

  function updateRow(index: number, patch: Partial<TransactionRuleConfig>) {
    writeRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  return (
    <section className="remittance-transaction-rules">
      <h3 className="remittance-transaction-rules__heading">Transaction rules</h3>

      {issues.rules.length > 0 && (
        <ul className="remittance-transaction-rules__issues" role="alert">
          {issues.rules.map((issue, i) => (
            <li key={`${issue.code}-${i}`}>{issue.message}</li>
          ))}
        </ul>
      )}

      <ol className="remittance-transaction-rules__rows">
        {rules.map((rule, index) => (
          <li key={rule.id} className="remittance-transaction-rules__row">
            <label className="remittance-transaction-rules__field">
              {`Rule ${index + 1} name`}
              <input
                aria-label={`Rule ${index + 1} name`}
                value={rule.name}
                onChange={(e) => updateRow(index, { name: e.target.value })}
              />
            </label>

            <label className="remittance-transaction-rules__field">
              {`Rule ${index + 1} severity`}
              <select
                aria-label={`Rule ${index + 1} severity`}
                value={rule.severity}
                onChange={(e) => updateRow(index, { severity: e.target.value as TransactionRuleConfig['severity'] })}
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.caption}
                  </option>
                ))}
              </select>
            </label>

            <ExpressionEditor
              label={`Rule ${index + 1} when`}
              value={rule.when}
              onChange={(when) => updateRow(index, { when })}
              pickerData={pickerData}
            />

            <label className="remittance-transaction-rules__field">
              {`Rule ${index + 1} message`}
              <input
                aria-label={`Rule ${index + 1} message`}
                value={rule.message}
                onChange={(e) => updateRow(index, { message: e.target.value })}
              />
            </label>

            <button
              type="button"
              className="remittance-transaction-rules__remove"
              onClick={() => handleRemove(index)}
              aria-label={`Remove rule ${index + 1}`}
            >
              Remove
            </button>
          </li>
        ))}
      </ol>

      <button type="button" className="remittance-transaction-rules__add" onClick={handleAdd}>
        Add rule
      </button>
    </section>
  );
}
